/**
 * GoTurkeyTransfer Analytics & Tracking Module
 * Single source of truth for GA4 + GTM ecommerce events
 * 
 * GTM Configuration:
 * 1. Trigger: Custom Event → event name "purchase"
 * 2. Tag: GA4 Event or Google Ads Conversion
 * 3. Variables: dataLayer → ecommerce.transaction_id, ecommerce.value, ecommerce.currency
 * 
 * Console test: window.GTT_DEBUG.firePurchaseTest()
 */

// ─── HELPERS ─────────────────────────────────────────────

const ensureDataLayer = () => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
  }
};

const safeNumber = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

const log = (tag, ...args) => {
  console.log(`%c[GTT Analytics] ${tag}`, 'color: #FF8C00; font-weight: bold;', ...args);
};

// ─── DEDUPE HELPERS ──────────────────────────────────────

const createDedupeKey = (eventName, id) => `gtt_${eventName}_fired_${id}`;

const isDuplicate = (eventName, id) => {
  if (typeof window === 'undefined') return true;
  const key = createDedupeKey(eventName, id);
  return !!(sessionStorage.getItem(key) || localStorage.getItem(key));
};

const markFired = (eventName, id) => {
  if (typeof window === 'undefined') return;
  const key = createDedupeKey(eventName, id);
  const ts = Date.now().toString();
  try { sessionStorage.setItem(key, ts); } catch (e) { /* quota */ }
  try { localStorage.setItem(key, ts); } catch (e) { /* quota */ }
};

// ─── GTM READINESS ───────────────────────────────────────

const waitForGTM = (maxRetries = 5, interval = 300) => {
  return new Promise((resolve) => {
    let attempts = 0;
    const check = () => {
      attempts++;
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        resolve(true);
        return;
      }
      if (attempts >= maxRetries) {
        log('GTM READINESS', `dataLayer not ready after ${maxRetries} retries — pushing anyway`);
        ensureDataLayer();
        resolve(true);
        return;
      }
      setTimeout(check, interval);
    };
    check();
  });
};

// ─── CORE PUSH ───────────────────────────────────────────

const pushEvent = (eventData) => {
  ensureDataLayer();
  window.dataLayer.push(eventData);
};

/** Push ecommerce event — clears ecommerce first (GA4 requirement) */
const pushEcommerceEvent = (eventData) => {
  ensureDataLayer();
  // GA4 requires ecommerce to be cleared before a new ecommerce push
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(eventData);
};

// ─── ITEM BUILDER ────────────────────────────────────────

const buildTransferItem = (data) => ({
  item_id: data.vehicleType || 'transfer',
  item_name: data.vehicleName || 'VIP Transfer',
  item_category: 'Airport Transfer',
  item_category2: data.route || '',
  price: safeNumber(data.price || data.value),
  quantity: 1,
});

// ─── PURCHASE HELPERS (exported for testing) ─────────────

export const shouldFirePurchase = (bookingRef) => {
  if (!bookingRef) {
    log('Purchase SKIPPED', 'missing bookingRef');
    return false;
  }
  if (isDuplicate('purchase', bookingRef)) {
    log('Purchase SKIPPED', `duplicate ${bookingRef}`);
    return false;
  }
  return true;
};

export const markPurchaseFired = (bookingRef) => {
  markFired('purchase', bookingRef);
};

export const createPurchasePayload = (data) => {
  const value = safeNumber(data.value);
  const currency = data.currency || 'EUR';
  const transactionId = data.transactionId;

  return {
    event: 'purchase',
    ecommerce: {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      tax: 0,
      shipping: 0,
      coupon: data.couponCode || '',
      items: [buildTransferItem(data)],
    },
    // Flat values for easy GTM variable access
    booking_ref: transactionId,
    booking_value: value,
    booking_currency: currency,
    transaction_id: transactionId,
    value: value,
    currency: currency,
    // Custom dimensions
    route: data.route || '',
    pickup: data.pickup || '',
    dropoff: data.dropoff || '',
    passengers: data.passengers || 1,
    payment_method: data.paymentMethod || '',
  };
};

// ─── A/B TEST ────────────────────────────────────────────

export const getABTestVariant = (testName) => {
  if (typeof window === 'undefined') return 'control';
  const storageKey = `ab_test_${testName}`;
  let variant = localStorage.getItem(storageKey);
  if (!variant) {
    const sessionId = sessionStorage.getItem('booking_session_id') || Math.random().toString();
    const hash = sessionId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    variant = hash % 2 === 0 ? 'control' : 'variant';
    localStorage.setItem(storageKey, variant);
  }
  return variant;
};

// ─── ANALYTICS EVENTS ────────────────────────────────────

export const AnalyticsEvents = {

  // ── Funnel step: Quote page ────────────────────────────
  beginQuote: (data = {}) => {
    pushEvent({
      event: 'begin_quote',
      page_path: '/quote',
      pickup: data.pickup || '',
      dropoff: data.dropoff || '',
      passengers: data.passengers || 1,
      timestamp: new Date().toISOString(),
    });
  },

  // ── Funnel step: Vehicle list visible ──────────────────
  viewVehicleStep: (data = {}) => {
    pushEvent({
      event: 'view_vehicle_step',
      available_vehicles: data.vehicleCount || 0,
      pickup: data.pickup || '',
      dropoff: data.dropoff || '',
      passengers: data.passengers || 1,
    });
  },

  // ── GA4 ecommerce: view_item ───────────────────────────
  viewItem: (data = {}) => {
    pushEcommerceEvent({
      event: 'view_item',
      ecommerce: {
        currency: 'EUR',
        value: safeNumber(data.price),
        items: [buildTransferItem(data)],
      },
    });
    log('view_item', data.vehicleName, safeNumber(data.price));
  },

  // ── Vehicle selected ───────────────────────────────────
  selectVehicle: (data) => {
    pushEvent({
      event: 'select_vehicle',
      vehicle_type: data.vehicleType || '',
      vehicle_name: data.vehicleName || '',
      price: safeNumber(data.price),
      currency: 'EUR',
      passengers: data.passengers || 1,
    });
  },

  // ── GA4 ecommerce: add_to_cart ─────────────────────────
  addToCart: (data = {}) => {
    pushEcommerceEvent({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'EUR',
        value: safeNumber(data.price),
        items: [buildTransferItem(data)],
      },
    });
    log('add_to_cart', data.vehicleName, safeNumber(data.price));
  },

  // ── Continue between steps (generic) ───────────────────
  continueToDetails: (data) => {
    pushEvent({
      event: 'continue_to_details',
      from_step: 'vehicle',
      to_step: 'extras',
      vehicle_type: data.vehicleType || '',
      price: safeNumber(data.price),
      currency: 'EUR',
      session_id: data.sessionId || '',
    });
  },

  continueStep: (fromStep, toStep, data = {}) => {
    pushEvent({
      event: 'continue_step',
      from_step: fromStep,
      to_step: toStep,
      value: safeNumber(data.value),
      currency: data.currency || 'EUR',
    });
  },

  // ── Extras / Details view steps ────────────────────────
  viewExtrasStep: (data = {}) => {
    pushEvent({
      event: 'view_extras_step',
      vehicle_type: data.vehicleType || '',
      base_price: safeNumber(data.basePrice),
    });
  },

  viewDetailsStep: (data = {}) => {
    pushEvent({
      event: 'view_details_step',
      vehicle_type: data.vehicleType || '',
      total_price: safeNumber(data.totalPrice),
    });
  },

  viewStep: (step, data = {}) => {
    pushEvent({
      event: 'view_step',
      step,
      value: safeNumber(data.value),
      currency: data.currency || 'EUR',
    });
  },

  // ── GA4 ecommerce: begin_checkout ──────────────────────
  beginCheckout: (data = {}) => {
    pushEcommerceEvent({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'EUR',
        value: safeNumber(data.totalPrice),
        coupon: data.couponCode || '',
        items: [buildTransferItem({ ...data, price: data.totalPrice })],
      },
    });
    log('begin_checkout', safeNumber(data.totalPrice));
  },

  // ── Payment method selected ────────────────────────────
  addPaymentInfo: (data) => {
    pushEcommerceEvent({
      event: 'add_payment_info',
      ecommerce: {
        currency: 'EUR',
        value: safeNumber(data.totalPrice),
        payment_type: data.paymentMethod || 'card',
        items: [buildTransferItem({ ...data, price: data.totalPrice })],
      },
    });
    log('add_payment_info', data.paymentMethod);
  },

  // ── GA4 ecommerce: purchase (MAIN CONVERSION) ─────────
  purchase: async (data) => {
    const bookingRef = data.transactionId;

    if (!shouldFirePurchase(bookingRef)) return false;

    // Mark as fired IMMEDIATELY to prevent race conditions
    // (e.g. React re-render triggering useEffect before async completes)
    markPurchaseFired(bookingRef);

    // Wait for GTM readiness
    await waitForGTM();

    const payload = createPurchasePayload(data);
    pushEcommerceEvent(payload);

    log('Purchase FIRED', bookingRef, {
      value: payload.ecommerce.value,
      currency: payload.ecommerce.currency,
    });

    return true;
  },

  // ── Utility events ─────────────────────────────────────
  whatsAppClick: (data = {}) => {
    pushEvent({
      event: 'whatsapp_click',
      click_location: data.location || 'floating',
      page_path: data.pagePath || window.location.pathname,
      booking_ref: data.bookingRef || '',
    });
  },

  helpModalShown: (data = {}) => {
    pushEvent({
      event: 'help_modal_shown',
      trigger_type: data.triggerType || 'exit_intent',
      page_path: data.pagePath || window.location.pathname,
    });
  },

  couponApplied: (data) => {
    pushEvent({
      event: 'coupon_applied',
      coupon_code: data.couponCode || '',
      discount_value: safeNumber(data.discountValue),
      discount_type: data.discountType || 'percent',
      order_value: safeNumber(data.orderValue),
    });
  },
};

// ─── GTT_DEBUG GLOBAL HOOK ───────────────────────────────

if (typeof window !== 'undefined') {
  window.GTT_DEBUG = {
    firePurchaseTest: () => {
      const testRef = `TEST_${Date.now()}`;
      log('DEBUG', `Firing test purchase: ${testRef}`);
      ensureDataLayer();
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(createPurchasePayload({
        transactionId: testRef,
        value: 99.99,
        currency: 'EUR',
        vehicleType: 'VAN',
        vehicleName: 'VIP Van (Test)',
        route: 'IST -> Taksim (Test)',
        pickup: 'Istanbul Airport (IST)',
        dropoff: 'Taksim',
        passengers: 2,
        paymentMethod: 'card',
      }));
      log('DEBUG', `Test purchase pushed. Check GTM Preview for event "purchase" with id=${testRef}`);
      return testRef;
    },

    getBookingTrackingState: () => {
      const state = {
        currentPath: window.location.pathname + window.location.search,
        dataLayerLength: (window.dataLayer || []).length,
        dataLayerEvents: (window.dataLayer || []).filter(e => e && e.event).map(e => e.event),
        gtmReady: Array.isArray(window.dataLayer),
        clarityReady: typeof window.clarity === 'function',
        dedupeKeys: {},
      };
      // Scan storage for dedupe keys
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('gtt_purchase_fired_')) {
          state.dedupeKeys[k] = localStorage.getItem(k);
        }
      }
      log('DEBUG', 'Tracking State:', state);
      return state;
    },

    clearDedupeKeys: () => {
      let cleared = 0;
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('gtt_purchase_fired_')) keysToRemove.push(k);
      }
      keysToRemove.forEach(k => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
        cleared++;
      });
      log('DEBUG', `Cleared ${cleared} dedupe keys`);
      return cleared;
    },

    inspectDataLayer: () => {
      const dl = window.dataLayer || [];
      const ecomEvents = dl.filter(e => e && e.ecommerce);
      log('DEBUG', `dataLayer has ${dl.length} entries, ${ecomEvents.length} ecommerce events`);
      ecomEvents.forEach((e, i) => {
        log('DEBUG', `Ecommerce #${i + 1}:`, e.event, e.ecommerce);
      });
      return ecomEvents;
    },
  };
}

export default AnalyticsEvents;
